import React, { useState, useEffect } from 'react';
import Select from '../ui/Select';
import axiosInstance from '../../api/axiosInstance';
import { toast } from 'react-toast';
import { setDateTimePrefs } from '../../utils/dateTimeFormat';

const AccountSettings = () => {
  const [accountData, setAccountData] = useState({
    username: '',
    language: '',
    timezone_id: null,
    dateFormat: '',
    timeFormat: ''
  });

  const [timezones, setTimezones] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState({ type: '', text: '' });

  // Enum options
  const languageOptions = [
    { value: 'English', label: 'English' },
    { value: 'Spanish', label: 'Spanish' },
    { value: 'French', label: 'French' },
    { value: 'German', label: 'German' },
    { value: 'Chinese', label: 'Chinese' },
    { value: 'Japanese', label: 'Japanese' },
    { value: 'Hindi', label: 'Hindi' }
  ];

  const dateFormatOptions = [
    { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY' },
    { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY' },
    { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD' },
    { value: 'YYYY/MM/DD', label: 'YYYY/MM/DD' },
    { value: 'DD-MMM-YYYY', label: 'DD-MMM-YYYY' }
  ];

  const timeFormatOptions = [
    { value: '12-hour (AM/PM)', label: '12-hour (AM/PM)' },
    { value: '24-hour', label: '24-hour' }
  ];

  // Fetch account settings and timezones on component load
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // Fetch timezones
        const timezonesResponse = await axiosInstance.get('/users/list_timezones');
        if (timezonesResponse?.detail?.timezones) {
          setTimezones(timezonesResponse?.detail.timezones);
        }

        // Fetch account settings
        const settingsResponse = await axiosInstance.get('/users/get_account_settings');
        if (settingsResponse?.detail?.account_settings) {
          const settings = settingsResponse?.detail.account_settings;
          setAccountData({
            username: settings.username || '',
            language: settings.language || '',
            timezone_id: settings.timezone_id || null,
            dateFormat: settings.date_format || '',
            timeFormat: settings.time_format || ''
          });
          setDateTimePrefs(settings);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        setSaveMessage({
          type: 'error',
          text: 'Failed to load account settings. Please try again.'
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setAccountData({
      ...accountData,
      [name]: value
    });
    
    // Clear error for this field if it exists
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {}
    
    if (!accountData.username.trim()) {
      newErrors.username = 'Username is required';
    } else if (accountData.username.length < 3) {
      newErrors.username = 'Username must be at least 3 characters';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      try {
        setSaving(true);
        setSaveMessage({ type: '', text: '' });

        // Prepare payload for API
        const payload = {
          timezone_id: accountData.timezone_id,
          language: accountData.language || null,
          date_format: accountData.dateFormat || null,
          time_format: accountData.timeFormat || null
        };

        // Call API to save settings
        const response = await axiosInstance.post('/users/add_account_settings', payload);
        
        if (response?.status === 200) {
          // setSaveMessage({
          //   type: 'success',
          //   text: 'Account settings updated successfully!'
          // });
          toast.success('Account settings updated successfully!');
          // Apply the new formats app-wide right away (fall back to what was submitted)
          setDateTimePrefs(response?.detail?.account_settings || payload);
          // Optionally update the form with the response data
          if (response?.detail?.account_settings) {
            const settings = response?.detail.account_settings;
            setAccountData({
              username: settings.username || accountData.username,
              language: settings.language || '',
              timezone_id: settings.timezone_id || null,
              dateFormat: settings.date_format || '',
              timeFormat: settings.time_format || ''
            });
          }
        }
      } catch (error) {
        console.error('Error saving account settings:', error);
        setSaveMessage({
          type: 'error',
          text: error.response?.detail?.message || 'Failed to save account settings. Please try again.'
        });
      } finally {
        setSaving(false);
      }
    }
  };

  return (
    <div className="settings-section">
      <div className="settings-section-intro">
        <h2 className="settings-section-title">Preferences</h2>
        <p className="settings-subtitle">Account preferences and defaults.</p>
      </div>
      
      {loading && <div className="loading-message">Loading account settings...</div>}
      
      {saveMessage.text && (
        <div className={`save-message ${saveMessage.type}`}>
          {saveMessage.text}
        </div>
      )}
      
      {!loading && (
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label className="form-label" htmlFor="username">Username</label>
          <input
            type="text"
            id="username"
            name="username"
            className={`form-input ${errors.username ? 'error' : ''}`}
            value={accountData.username}
            onChange={handleInputChange}
            disabled
          />
          {errors.username && <div className="form-error">{errors.username}</div>}
          {/* <div className="form-hint">This will be your unique identifier across the platform.</div> */}
        </div>
        
        <div className="form-group">
          <label className="form-label" htmlFor="language">
            Language
            <span style={{ color: 'red', fontWeight: 'bold', marginLeft: '0.5rem', cursor: 'help' }} title="Coming Soon">*</span>
          </label>
          <Select
            id="language"
            name="language"
            className="rt-select--form"
            value={accountData.language}
            onChange={handleInputChange}
            disabled
          >
            <option value="">Select Language</option>
            {languageOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </div>
        
        <div className="form-group">
          <label className="form-label" htmlFor="timezone_id">Timezone</label>
          <Select
            id="timezone_id"
            name="timezone_id"
            className="rt-select--form"
            value={accountData.timezone_id || ''}
            onChange={(e) => handleInputChange({ target: { name: 'timezone_id', value: e.target.value ? parseInt(e.target.value) : null } })}
          >
            <option value="">Select Timezone</option>
            {timezones.map(tz => (
              <option key={tz.id} value={tz.id}>
                {tz.display_name}
              </option>
            ))}
          </Select>
        </div>
        
        <div className="form-row">
          <div className="form-col">
            <div className="form-group">
              <label className="form-label" htmlFor="dateFormat">
                Date Format
                {/* <span style={{ color: 'red', fontWeight: 'bold', marginLeft: '0.5rem', cursor: 'help' }} title="Coming Soon">*</span> */}
              </label>
              <Select
                id="dateFormat"
                name="dateFormat"
                className="rt-select--form"
                value={accountData.dateFormat}
                onChange={handleInputChange}
                // disabled
              >
                <option value="">Select Date Format</option>
                {dateFormatOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </div>
          </div>
          <div className="form-col">
            <div className="form-group">
              <label className="form-label" htmlFor="timeFormat">
                Time Format
                {/* <span style={{ color: 'red', fontWeight: 'bold', marginLeft: '0.5rem', cursor: 'help' }} title="Coming Soon">*</span> */}
              </label>
              <Select
                id="timeFormat"
                name="timeFormat"
                className="rt-select--form"
                value={accountData.timeFormat}
                onChange={handleInputChange}
                // disabled
              >
                <option value="">Select Time Format</option>
                {timeFormatOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </div>
          </div>
        </div>
        
        <div className="settings-card">
          <h3 className="settings-card-title">
            Export Your Data
            <span style={{ color: 'red', fontWeight: 'bold', marginLeft: '0.5rem', cursor: 'help' }} title="Coming Soon">*</span>
          </h3>
          <p className="settings-card-description">
            Download a copy of your data including test cases, test suites, and reports.
          </p>
          <button type="button" className="btn btn-outline" disabled>
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
              <polyline points="7 10 12 15 17 10"></polyline>
              <line x1="12" y1="15" x2="12" y2="3"></line>
            </svg>
            Export Data
          </button>
        </div>
        
        <div className="danger-zone">
          <h3 className="danger-zone-title">
            Danger Zone
            <span style={{ color: 'red', fontWeight: 'bold', marginLeft: '0.5rem', cursor: 'help' }} title="Coming Soon">*</span>
          </h3>
          <p className="danger-zone-description">
            Once you delete your account, there is no going back. Please be certain.
          </p>
          <button type="button" className="btn btn-outline-danger" disabled>
            Delete Account
          </button>
        </div>
        
        <div className="form-actions">
          <button type="button" className="btn btn-outline" disabled={saving}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
      )}
    </div>
  );
};

export default AccountSettings;